import React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import happinessSurveyValues, {
  optionalFitFeedBackInputOptionsV2,
  likedTheReview,
} from "constants/happiness-survey-values";

import InputContainer from "./inputs/input-container";
import CapsuleSelect from "./inputs/capsule-select.tsx";

import { addFeedback } from "actions/happiness-survey-actions";
import { happinessSurveyContainsFeedback } from "helpers/feedback-helper";
import { convertCurrentSurveyIntoForm, isLocalValueSelected } from "helpers/happiness-survey-helpers";
import FeedbackFooter from "./feedback-footer";
import SurveyItemHeader from "./feedback-header";
import SurveyItemBanner from "./item-banner";
import InputsLabel from "./inputs/input-label";

const propTypes = {
  currentSurveyIndex: PropTypes.number.isRequired,
  closeFeedbackScreen: PropTypes.func.isRequired,
};

const optionalFitFeedBackInputKeys = Object.keys(optionalFitFeedBackInputOptionsV2);
const fitRateKeys = [
  happinessSurveyValues.feedbackTypes.wornHappinessLevel,
  happinessSurveyValues.feedbackTypes.overallFit,
  ...optionalFitFeedBackInputKeys,
];
const defaultForm = fitRateKeys.reduce(
  (prev, curr) => ({
    ...prev,
    [curr]: "",
  }),
  {}
);

function FitRate({ currentSurveyIndex, closeFeedbackScreen = () => {} }) {
  const currentSurvey = useSelector(state => state.happinessSurvey?.itemSurveys?.[currentSurveyIndex]);
  if (!currentSurvey || !currentSurvey.bookingId) {
    return null;
  }

  const [form, setForm] = React.useState(convertCurrentSurveyIntoForm(currentSurvey, defaultForm, {}));
  const dispatch = useDispatch();

  React.useEffect(() => {
    const el = document.getElementById("hs-fit-rate-root");
    el?.scrollTo?.(0, 0);
    window.scrollTo?.(0, 0);
  }, []);

  const saveFeedbackToRedux = () => {
    for (const key in form) {
      if (form[key] && !happinessSurveyContainsFeedback(currentSurvey, form[key])) {
        // dispatching addFeedback does two things:
        // 1. with a declared type will remove all feedback related to that key
        // 2a. dispatching with a new value will add the feedback value to the survey
        // 2b. dispatching with a null value will do nothing
        dispatch(addFeedback(form[key], currentSurvey.bookingId, key, {}));
      }
    }
  };

  const handleSelect = (value, type) => {
    setForm(oldForm => ({
      ...oldForm,
      [type]: oldForm[type] === value ? "" : value,
    }));
  };

  const likedTheProducts = {
    headerText: "What did you think of this item?",
    isRequired: false,
  };

  const handleSave = () => {
    saveFeedbackToRedux();
    closeFeedbackScreen();
  };

  return (
    <div
      id="hs-fit-rate-root"
      className="flex flex-col h-full overflow-auto"
      data-test-id="happiness-survey-fit-rate-screen">
      <SurveyItemHeader
        title="Add fit rating"
        subtitle="Share more so we can give you recommendations on what to rent next."
        closeFeedbackScreen={closeFeedbackScreen}
      />
      <div className="flex flex-col px-4 grow">
        <SurveyItemBanner currentSurvey={currentSurvey} />
        <div className="py-6 gap-4 flex flex-col justify-start">
          <InputContainer labelProps={likedTheProducts} additionalClassName="!pb-2">
            <CapsuleSelect
              isOptionsOnly={true}
              onSelect={handleSelect}
              options={likedTheReview.map(option => ({
                ...option,
                selected: isLocalValueSelected(
                  form,
                  option.value,
                  happinessSurveyValues.feedbackTypes.wornHappinessLevel
                ),
              }))}
              type={happinessSurveyValues.feedbackTypes.wornHappinessLevel}
            />
          </InputContainer>
          <InputsLabel
            headerText="How did it fit?"
            subHeaderText="Ratings are included anonymously in fit recommendations to help you and other members find the perfect fit."
          />
          {optionalFitFeedBackInputKeys.map(key => {
            const inputObject = optionalFitFeedBackInputOptionsV2[key];
            const options = inputObject.options.map(option => ({
              ...option,
              selected: isLocalValueSelected(form, option.value, key),
            }));
            const labelProps = {
              subHeaderText: inputObject.title,
              additionalClassName: {
                subHeader: "!mt-0",
              },
            };

            return (
              <InputContainer labelProps={labelProps} key={key} additionalClassName="!pb-0">
                <CapsuleSelect
                  isOptionsOnly={true}
                  onSelect={handleSelect}
                  options={options}
                  type={key}
                  typeAsText={inputObject.title}
                />
              </InputContainer>
            );
          })}
        </div>
      </div>
      <FeedbackFooter ctaCopy="Save Rating" onClick={handleSave} />
    </div>
  );
}

FitRate.pageName = happinessSurveyValues.pages.RATE_IT;
FitRate.propTypes = propTypes;

export default FitRate;
